import React, { useState } from 'react';
import { Button, Card, Form, Modal, Select, Space, Table, message } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import {
    DELETE_CLIENT_BILL_EXPORT_TEMPLATE_CONFIG,
    LIST_CLIENT_BILL_EXPORT_TEMPLATE_CONFIGS,
    UPSERT_CLIENT_BILL_EXPORT_TEMPLATE_CONFIG,
} from '../../../graphql/queries/settlement/billExportTemplateConfigQueries';
import { BillTypeEnum } from '../../../graphql/types/settlement/CommonTypes';
import { BillExportTemplateType } from '../../../graphql/types/settlement/SettlementExportType';
import { SettlementClientRoleType } from './config/settlementClientRoleType';
import { hasResource } from '../../../utils/resourceAuth';
import { ResourceCode } from '../../../constants/resourceCodes';
import { ClientSugSelector } from '../../../components/settlement/ClientSugSelector';

const BILL_TYPE_OPTIONS = [
    { value: BillTypeEnum.DELIVERY_FEE_BILL, label: '配送费账单' },
];

const DELIVERY_TEMPLATE_OPTIONS = [
    { value: BillExportTemplateType.TEMU_TEMPLATE, label: 'Temu模板' },
    { value: BillExportTemplateType.TEMU_HALF_MANAGED_TEMPLATE, label: 'Temu半托管模板' },
    { value: BillExportTemplateType.GENERAL_TEMPLATE, label: '一般客户模板' },
    { value: BillExportTemplateType.CUSTOMER_STANDARD_TEMPLATE, label: '客户标准模板' },
    { value: BillExportTemplateType.TIKTOK_TEMPLATE, label: 'TikTok客户模板' },
    { value: BillExportTemplateType.JD_TEMPLATE, label: '京东客户模板' },
];

const TEMPLATE_LABEL: Record<string, string> = {
    TEMU_TEMPLATE: 'Temu模板',
    TEMU_HALF_MANAGED_TEMPLATE: 'Temu半托管模板',
    GENERAL_TEMPLATE: '一般客户模板',
    CUSTOMER_STANDARD_TEMPLATE: '客户标准模板',
    TIKTOK_TEMPLATE: 'TikTok客户模板',
    JD_TEMPLATE: '京东客户模板',
    SLA_TEMPLATE: 'SLA模板',
    DSP_DELIVERY_TEMPLATE: 'DSP运费模板',
};

const BillExportTemplateConfigPage: React.FC = () => {
    const canOperate = hasResource([
        ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_OPERATE,
        ResourceCode.menu.OPS_TOOL_SETTLEMENT,
    ]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [filterClientId, setFilterClientId] = useState<string | undefined>();
    const [modalOpen, setModalOpen] = useState(false);
    const [form] = Form.useForm();

    const { data, loading, refetch } = useQuery(LIST_CLIENT_BILL_EXPORT_TEMPLATE_CONFIGS, {
        variables: {
            settlementClientId: filterClientId,
            pageRequest: { page, pageSize },
        },
        fetchPolicy: 'network-only',
    });

    const [upsertConfig, { loading: saving }] = useMutation(UPSERT_CLIENT_BILL_EXPORT_TEMPLATE_CONFIG);
    const [deleteConfig] = useMutation(DELETE_CLIENT_BILL_EXPORT_TEMPLATE_CONFIG);

    const handleFilterClientChange = (value: string | string[]) => {
        const clientId = Array.isArray(value) ? value[0] : value;
        setFilterClientId(clientId || undefined);
        setPage(1);
    };

    const openCreate = () => {
        form.resetFields();
        form.setFieldsValue({
            billType: BillTypeEnum.DELIVERY_FEE_BILL,
            exportTemplate: BillExportTemplateType.GENERAL_TEMPLATE,
        });
        setModalOpen(true);
    };

    const handleSave = async () => {
        const values = await form.validateFields();
        await upsertConfig({
            variables: {
                settlementClientId: values.settlementClientId,
                billType: values.billType,
                exportTemplate: values.exportTemplate,
            },
        });
        message.success('保存成功');
        setModalOpen(false);
        await refetch();
    };

    const handleDelete = (id: string) => {
        Modal.confirm({
            title: '确认删除该配置？',
            onOk: async () => {
                await deleteConfig({ variables: { id } });
                message.success('已删除');
                await refetch();
            },
        });
    };

    const columns = [
        { title: '客户名称', dataIndex: 'clientName', key: 'clientName', width: 200, ellipsis: true },
        {
            title: '账单类型',
            dataIndex: 'billType',
            key: 'billType',
            width: 140,
            render: (value: BillTypeEnum) =>
                value === BillTypeEnum.DELIVERY_FEE_BILL ? '配送费账单' : value,
        },
        {
            title: '导出模板',
            dataIndex: 'exportTemplate',
            key: 'exportTemplate',
            width: 180,
            render: (value: string) => TEMPLATE_LABEL[value] ?? value,
        },
        { title: '更新时间（UTC）', dataIndex: 'updatedAt', key: 'updatedAt', width: 200 },
        canOperate
            ? {
                  title: '操作',
                  key: 'actions',
                  width: 100,
                  render: (_: unknown, record: { id: string }) => (
                      <Button type="link" danger onClick={() => handleDelete(record.id)}>
                          删除
                      </Button>
                  ),
              }
            : null,
    ].filter(Boolean);

    return (
        <Card
            title="客户账单导出模板配置"
            extra={
                canOperate && (
                    <Button type="primary" onClick={openCreate}>
                        新增配置
                    </Button>
                )
            }
        >
            <Space wrap className="mb-4">
                <ClientSugSelector
                    roleType={SettlementClientRoleType.CHANNEL_CLIENT}
                    placeholder="筛选客户（名称 / channelId）"
                    className="w-[280px]"
                    value={filterClientId}
                    onChange={handleFilterClientChange}
                />
                <Button onClick={() => refetch()}>刷新</Button>
            </Space>

            <Table
                rowKey="id"
                loading={loading}
                columns={columns as never}
                dataSource={data?.listClientBillExportTemplateConfigs?.content ?? []}
                pagination={{
                    current: page,
                    pageSize,
                    total: data?.listClientBillExportTemplateConfigs?.totalElements ?? 0,
                    showSizeChanger: true,
                    onChange: (p, size) => {
                        setPage(p);
                        setPageSize(size);
                    },
                }}
            />

            <Modal
                title="配置导出模板"
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={handleSave}
                confirmLoading={saving}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="settlementClientId"
                        label="客户"
                        rules={[{ required: true, message: '请选择客户' }]}
                    >
                        <ClientSugSelector
                            roleType={SettlementClientRoleType.CHANNEL_CLIENT}
                            placeholder="选择客户（名称 / channelId）"
                        />
                    </Form.Item>
                    <Form.Item
                        name="billType"
                        label="账单类型"
                        rules={[{ required: true, message: '请选择账单类型' }]}
                    >
                        <Select options={BILL_TYPE_OPTIONS} />
                    </Form.Item>
                    <Form.Item
                        name="exportTemplate"
                        label="导出模板"
                        rules={[{ required: true, message: '请选择导出模板' }]}
                    >
                        <Select options={DELIVERY_TEMPLATE_OPTIONS} />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default BillExportTemplateConfigPage;
